import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Typography } from "@mui/material";
import { Hero } from "../components/ui/Hero";
import { usePostHog } from "../components/widgets/PostHogProvider";
import { useSEO, useBreadcrumbSchema, PageSEO } from "../lib/seo";
import { StickyPricingTabs } from "../components/sections/pricing/StickyPricingTabs";
import { PricingProfessionalsView } from "../components/sections/pricing/views/PricingProfessionalsView";
import { PricingIndividualsView } from "../components/sections/pricing/views/PricingIndividualsView";
import { PricingCaseManagerView } from "../components/sections/pricing/views/PricingCaseManagerView";

type PricingSearch = {
  tab?: 'individuals' | 'professionals' | 'complete'
}

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  validateSearch: (search: Record<string, unknown>): PricingSearch => {
    return {
      tab: (search.tab as PricingSearch['tab']) || 'professionals',
    }
  },
});

function PricingPage() {
  useSEO(PageSEO.pricing);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Pricing', url: '/pricing' }
  ]);

  const navigate = useNavigate({ from: Route.fullPath });
  const { tab } = Route.useSearch();
  const { capture } = usePostHog();

  const tabValue = tab === 'professionals' ? 0 : tab === 'individuals' ? 1 : 2;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newTab = newValue === 0 ? 'professionals' : newValue === 1 ? 'individuals' : 'complete';
    navigate({ search: { tab: newTab } });
    capture('pricing_tab_changed', { tab: newTab === 'complete' ? 'legal' : newTab } as any);
  };

  return (
    <>
      <Hero
        tagline="Simple, Transparent Pricing"
        taglineSx={{ fontSize: { xs: '0.9rem' }, fontWeight: 700, mb: 0 }}
        title="Whether you're recovering, treating, or funding care—we have a plan for you."
        titleSx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 600 }}
        variant="gradient"
        subtitleMb={2}
        childrenMt={0}
        renderTitle={(title: string) => <Typography variant="h4" component="h1" gutterBottom fontWeight={800}>{title}</Typography>}
        sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 12, md: 14 } }}
      >
      </Hero>

      <StickyPricingTabs offset={11} value={tabValue} onChange={handleTabChange} />

      {/* --- TAB 1: PROFESSIONALS & TEAMS --- */}
      {tabValue === 0 && (
        <div role="tabpanel" id="pricing-panel-professionals" aria-labelledby="pricing-tab-professionals">
          <PricingProfessionalsView />
        </div>
      )}

      {/* --- TAB 2: INDIVIDUALS & FAMILIES --- */}
      {tabValue === 1 && (
        <div role="tabpanel" id="pricing-panel-individuals" aria-labelledby="pricing-tab-individuals">
          <PricingIndividualsView />
        </div>
      )}

      {/* --- TAB 3: CASE MANAGERS --- */}
      {tabValue === 2 && (
        <div role="tabpanel" id="pricing-panel-complete" aria-labelledby="pricing-tab-complete">
          <PricingCaseManagerView />
        </div>
      )}
    </>
  );
}
